/**
 * The few words the TypeScript writes onto a page itself, in the page's
 * language (SEO_BRIEF_04.md item 15).
 *
 * Almost every sentence on this site is server-rendered, so the Jinja string
 * table (server/app/i18n_strings.py) already translates it: the banners are
 * cloned from the page's own state bank, the button labels are read from the
 * button. What is left is what the code supplies: the Turnstile card's line,
 * the line a tool page shows while an image subtitle's reader gets ready, the kind of file and the
 * page name B5_IMAGE_SUBTITLE is filled with, and the platform noun GET
 * /api/platforms returns in English ("TikTok video"), which E1_PLATFORM puts
 * in its title.
 *
 * Keyed by the English and by `<html lang>`, which the server sets on the
 * translated pages. On every other page the language is `en` and `t` hands
 * back exactly what it was given, so no English page can change.
 *
 * NATIVE SPEAKER REVIEW RECOMMENDED, as for the server table.
 *
 * Dutch and Swedish (SEO_BRIEF_10A section 4) are a module each, `{english: translation}` for
 * every key (lib/i18n-nl.ts, lib/i18n-sv.ts), as the server's are (server/app/i18n_strings_nl.py,
 * i18n_strings_sv.py), and `withModules` puts each word into its key's entry, so the entries
 * below stay as they were written.
 */

import { NL } from './i18n-nl.ts';
import { SV } from './i18n-sv.ts';

/** The languages each entry below names itself. */
type RowLang = 'id' | 'pt' | 'es' | 'ko' | 'fr' | 'ja' | 'it' | 'de' | 'tr' | 'vi';
/** The languages written as a module of their own. */
type ModuleLang = 'nl' | 'sv';

export type PageLang = 'en' | RowLang | ModuleLang;

type Row = Readonly<Record<RowLang, string>>;
type Translations = Row & Readonly<Partial<Record<ModuleLang, string>>>;

const MODULES: Readonly<Record<ModuleLang, Readonly<Record<string, string>>>> = { nl: NL, sv: SV };

/** The languages with pages, server/locales.py LOCALES: the entries' ten, then the modules'. */
const PAGE_LANGS: readonly string[] = [
  ...['id', 'pt', 'es', 'ko', 'fr', 'ja', 'it', 'de', 'tr', 'vi'],
  ...Object.keys(MODULES),
];

const ROWS: Readonly<Record<string, Row>> = {
  // lib/session.ts TURNSTILE_PROMPT, the bot-check card's line while the box wants a click.
  // It names no provider (the founder's ruling: no infrastructure names in UI text).
  "We need a quick check that you're a person. Tick the box below.": {
    id: 'Kami perlu memastikan sebentar bahwa Anda manusia. Centang kotak di bawah.',
    pt: 'Precisamos confirmar rapidinho que você é uma pessoa. Marque a caixa abaixo.',
    es: 'Necesitamos comprobar rápido que eres una persona. Marca la casilla de abajo.',
    ko: '사람인지 간단히 확인하겠습니다. 아래 상자에 체크하세요.',
    fr: 'Nous devons vérifier rapidement que vous êtes une personne. Cochez la case ci-dessous.',
    ja: '人による操作かどうか、簡単な確認をお願いします。下のボックスにチェックを入れてください。',
    it: 'Dobbiamo verificare al volo che tu sia una persona. Spunta la casella qui sotto.',
    de: 'Wir müssen kurz prüfen, ob Sie ein Mensch sind. Setzen Sie unten das Häkchen.',
    tr: 'İnsan olduğunuzu hızlıca kontrol etmemiz gerekiyor. Aşağıdaki kutuyu işaretleyin.',
    vi: 'Chúng tôi cần kiểm tra nhanh rằng bạn là người thật. Hãy đánh dấu vào ô bên dưới.',
  },
  // pages/tool.ts, while an image subtitle's reader gets ready (SEO_BRIEF_04 item 8). It says
  // nothing about what loads (the founder's ruling of 2026-10-27: no page says how a tool works).
  'Getting ready…': {
    id: 'Bersiap…',
    pt: 'Preparando…',
    es: 'Preparando…',
    ko: '준비하는 중…',
    fr: 'Préparation…',
    ja: '準備しています…',
    it: 'Preparazione…',
    de: 'Wird vorbereitet…',
    tr: 'Hazırlanıyor…',
    vi: 'Đang chuẩn bị…',
  },
  // pages/tool.ts, B5_IMAGE_SUBTITLE's {kind} and {page} for an image subtitle dropped on a page
  // that reads text. Added with the video and OCR work: NATIVE SPEAKER REVIEW RECOMMENDED, as
  // for the rest. The two page names are also in the server table (B5_UNSUPPORTED_BINARY,
  // B5_IMAGE_TRACK); keep them the same in both.
  'a SUP/PGS subtitle file': {
    id: 'file subtitle SUP/PGS',
    pt: 'um arquivo de legenda SUP/PGS',
    es: 'un archivo de subtítulos SUP/PGS',
    ko: 'SUP/PGS 자막 파일',
    fr: 'un fichier de sous-titres SUP/PGS',
    ja: 'SUP/PGS字幕ファイル',
    it: 'un file di sottotitoli SUP/PGS',
    de: 'eine SUP/PGS-Untertiteldatei',
    tr: 'bir SUP/PGS altyazı dosyası',
    vi: 'tệp phụ đề SUP/PGS',
  },
  'a VobSub (IDX/SUB) subtitle file': {
    id: 'file subtitle VobSub (IDX/SUB)',
    pt: 'um arquivo de legenda VobSub (IDX/SUB)',
    es: 'un archivo de subtítulos VobSub (IDX/SUB)',
    ko: 'VobSub(IDX/SUB) 자막 파일',
    fr: 'un fichier de sous-titres VobSub (IDX/SUB)',
    ja: 'VobSub（IDX/SUB）字幕ファイル',
    it: 'un file di sottotitoli VobSub (IDX/SUB)',
    de: 'eine VobSub-Untertiteldatei (IDX/SUB)',
    tr: 'bir VobSub (IDX/SUB) altyazı dosyası',
    vi: 'tệp phụ đề VobSub (IDX/SUB)',
  },
  'SUP to SRT': {
    id: 'SUP ke SRT',
    pt: 'SUP para SRT',
    es: 'SUP a SRT',
    ko: 'SUP를 SRT로 변환',
    fr: 'SUP en SRT',
    ja: 'SUPをSRTに変換',
    it: 'Da SUP a SRT',
    de: 'SUP in SRT',
    tr: "SUP'tan SRT'ye",
    vi: 'SUP sang SRT',
  },
  'IDX/SUB to SRT': {
    id: 'IDX/SUB ke SRT',
    pt: 'IDX/SUB para SRT',
    es: 'IDX/SUB a SRT',
    ko: 'IDX/SUB를 SRT로 변환',
    fr: 'IDX/SUB en SRT',
    ja: 'IDX/SUBをSRTに変換',
    it: 'Da IDX/SUB a SRT',
    de: 'IDX/SUB in SRT',
    tr: "IDX/SUB'dan SRT'ye",
    vi: 'IDX/SUB sang SRT',
  },
  // The H1 nouns of server/platforms.py PAGE_NOUN, as E1_PLATFORM's {noun}.
  'YouTube video': {
    id: 'video YouTube',
    pt: 'vídeo do YouTube',
    es: 'video de YouTube',
    ko: 'YouTube 동영상',
    fr: 'vidéo YouTube',
    ja: 'YouTube動画',
    it: 'video di YouTube',
    de: 'YouTube-Video',
    tr: 'YouTube videosu',
    vi: 'video YouTube',
  },
  'TikTok video': {
    id: 'video TikTok',
    pt: 'vídeo do TikTok',
    es: 'video de TikTok',
    ko: 'TikTok 동영상',
    fr: 'vidéo TikTok',
    ja: 'TikTok動画',
    it: 'video di TikTok',
    de: 'TikTok-Video',
    tr: 'TikTok videosu',
    vi: 'video TikTok',
  },
  'Instagram video': {
    id: 'video Instagram',
    pt: 'vídeo do Instagram',
    es: 'video de Instagram',
    ko: 'Instagram 동영상',
    fr: 'vidéo Instagram',
    ja: 'Instagram動画',
    it: 'video di Instagram',
    de: 'Instagram-Video',
    tr: 'Instagram videosu',
    vi: 'video Instagram',
  },
  'Facebook video': {
    id: 'video Facebook',
    pt: 'vídeo do Facebook',
    es: 'video de Facebook',
    ko: 'Facebook 동영상',
    fr: 'vidéo Facebook',
    ja: 'Facebook動画',
    it: 'video di Facebook',
    de: 'Facebook-Video',
    tr: 'Facebook videosu',
    vi: 'video Facebook',
  },
  'X video': {
    id: 'video X',
    pt: 'vídeo do X',
    es: 'video de X',
    ko: 'X 동영상',
    fr: 'vidéo X',
    ja: 'Xの動画',
    it: 'video di X',
    de: 'X-Video',
    tr: 'X videosu',
    vi: 'video X',
  },
  'Vimeo video': {
    id: 'video Vimeo',
    pt: 'vídeo do Vimeo',
    es: 'video de Vimeo',
    ko: 'Vimeo 동영상',
    fr: 'vidéo Vimeo',
    ja: 'Vimeo動画',
    it: 'video di Vimeo',
    de: 'Vimeo-Video',
    tr: 'Vimeo videosu',
    vi: 'video Vimeo',
  },
  'Twitch video': {
    id: 'video Twitch',
    pt: 'vídeo da Twitch',
    es: 'video de Twitch',
    ko: 'Twitch 동영상',
    fr: 'vidéo Twitch',
    ja: 'Twitch動画',
    it: 'video di Twitch',
    de: 'Twitch-Video',
    tr: 'Twitch videosu',
    vi: 'video Twitch',
  },
  'Loom video': {
    id: 'video Loom',
    pt: 'vídeo do Loom',
    es: 'video de Loom',
    ko: 'Loom 동영상',
    fr: 'vidéo Loom',
    ja: 'Loom動画',
    it: 'video di Loom',
    de: 'Loom-Video',
    tr: 'Loom videosu',
    vi: 'video Loom',
  },
  'Dailymotion video': {
    id: 'video Dailymotion',
    pt: 'vídeo do Dailymotion',
    es: 'video de Dailymotion',
    ko: 'Dailymotion 동영상',
    fr: 'vidéo Dailymotion',
    ja: 'Dailymotion動画',
    it: 'video di Dailymotion',
    de: 'Dailymotion-Video',
    tr: 'Dailymotion videosu',
    vi: 'video Dailymotion',
  },
  'Rumble video': {
    id: 'video Rumble',
    pt: 'vídeo do Rumble',
    es: 'video de Rumble',
    ko: 'Rumble 동영상',
    fr: 'vidéo Rumble',
    ja: 'Rumble動画',
    it: 'video di Rumble',
    de: 'Rumble-Video',
    tr: 'Rumble videosu',
    vi: 'video Rumble',
  },
  'LinkedIn video': {
    id: 'video LinkedIn',
    pt: 'vídeo do LinkedIn',
    es: 'video de LinkedIn',
    ko: 'LinkedIn 동영상',
    fr: 'vidéo LinkedIn',
    ja: 'LinkedIn動画',
    it: 'video di LinkedIn',
    de: 'LinkedIn-Video',
    tr: 'LinkedIn videosu',
    vi: 'video LinkedIn',
  },
  // pages/subtitle-translate.ts (SEO_BRIEF_09 section 2.3): what the subtitle translator says once
  // a translation is back, with the target's or the file's language named in the page's own
  // language (Intl.DisplayNames). Every other word on that page is the template's.
  'Translated into {language}.': {
    id: 'Diterjemahkan ke bahasa {language}.',
    pt: 'Traduzido para o {language}.',
    es: 'Traducido al {language}.',
    ko: '{language} 번역을 마쳤습니다.',
    fr: 'Traduit en {language}.',
    ja: '{language}に翻訳しました。',
    it: 'Tradotto in {language}.',
    de: 'Auf {language} übersetzt.',
    tr: '{language} diline çevrildi.',
    vi: 'Đã dịch sang {language}.',
  },
  "1 line couldn't be translated and is left in {language}.": {
    id: '1 baris tidak dapat diterjemahkan dan dibiarkan dalam bahasa {language}.',
    pt: '1 linha não pôde ser traduzida e ficou em {language}.',
    es: '1 línea no se pudo traducir y queda en {language}.',
    ko: '1줄은 번역하지 못해 {language} 그대로 두었습니다.',
    fr: "1 ligne n'a pas pu être traduite et reste en {language}.",
    ja: '1行は翻訳できなかったため、{language}のままです。',
    it: '1 riga non è stata tradotta e resta in {language}.',
    de: '1 Zeile konnte nicht übersetzt werden und bleibt auf {language}.',
    tr: '1 satır çevrilemedi ve {language} olarak bırakıldı.',
    vi: '1 dòng không dịch được và được giữ nguyên bằng {language}.',
  },
  "{n} lines couldn't be translated and are left in {language}.": {
    id: '{n} baris tidak dapat diterjemahkan dan dibiarkan dalam bahasa {language}.',
    pt: '{n} linhas não puderam ser traduzidas e ficaram em {language}.',
    es: '{n} líneas no se pudieron traducir y quedan en {language}.',
    ko: '{n}줄은 번역하지 못해 {language} 그대로 두었습니다.',
    fr: "{n} lignes n'ont pas pu être traduites et restent en {language}.",
    ja: '{n}行は翻訳できなかったため、{language}のままです。',
    it: '{n} righe non sono state tradotte e restano in {language}.',
    de: '{n} Zeilen konnten nicht übersetzt werden und bleiben auf {language}.',
    tr: '{n} satır çevrilemedi ve {language} olarak bırakıldı.',
    vi: '{n} dòng không dịch được và được giữ nguyên bằng {language}.',
  },
  "1 line couldn't be translated and is left as it was.": {
    id: '1 baris tidak dapat diterjemahkan dan dibiarkan seperti semula.',
    pt: '1 linha não pôde ser traduzida e ficou como estava.',
    es: '1 línea no se pudo traducir y queda como estaba.',
    ko: '1줄은 번역하지 못해 원래대로 두었습니다.',
    fr: "1 ligne n'a pas pu être traduite et reste telle quelle.",
    ja: '1行は翻訳できなかったため、元のままです。',
    it: "1 riga non è stata tradotta e resta com'era.",
    de: '1 Zeile konnte nicht übersetzt werden und bleibt, wie sie war.',
    tr: '1 satır çevrilemedi ve olduğu gibi bırakıldı.',
    vi: '1 dòng không dịch được và được giữ nguyên.',
  },
  "{n} lines couldn't be translated and are left as they were.": {
    id: '{n} baris tidak dapat diterjemahkan dan dibiarkan seperti semula.',
    pt: '{n} linhas não puderam ser traduzidas e ficaram como estavam.',
    es: '{n} líneas no se pudieron traducir y quedan como estaban.',
    ko: '{n}줄은 번역하지 못해 원래대로 두었습니다.',
    fr: "{n} lignes n'ont pas pu être traduites et restent telles quelles.",
    ja: '{n}行は翻訳できなかったため、元のままです。',
    it: "{n} righe non sono state tradotte e restano com'erano.",
    de: '{n} Zeilen konnten nicht übersetzt werden und bleiben, wie sie waren.',
    tr: '{n} satır çevrilemedi ve olduğu gibi bırakıldı.',
    vi: '{n} dòng không dịch được và được giữ nguyên.',
  },
  'This file is already in {language}.': {
    id: 'File ini sudah dalam bahasa {language}.',
    pt: 'Este arquivo já está em {language}.',
    es: 'Este archivo ya está en {language}.',
    ko: '이 파일은 이미 {language}입니다.',
    fr: 'Ce fichier est déjà en {language}.',
    ja: 'このファイルはすでに{language}です。',
    it: 'Questo file è già in {language}.',
    de: 'Diese Datei ist bereits auf {language}.',
    tr: 'Bu dosya zaten {language}.',
    vi: 'Tệp này đã bằng {language}.',
  },
  podcast: {
    id: 'podcast',
    pt: 'podcast',
    es: 'pódcast',
    ko: '팟캐스트',
    fr: 'podcast',
    ja: 'ポッドキャスト',
    it: 'podcast',
    de: 'Podcast',
    tr: 'podcast',
    vi: 'podcast',
  },
  // lib/gemini.ts limitMessage(): what the page says when the language model can't take a request
  // (SEO_BRIEF_09 2.2, worded by the founder's ruling of 2026-10-26: nothing about limits), on the
  // translated subtitle-translator pages. {reset} is filled in by the code. NATIVE SPEAKER REVIEW
  // RECOMMENDED, as for the rest.
  "Translation and summaries aren't available right now.": {
    id: 'Terjemahan dan ringkasan sedang tidak tersedia.',
    pt: 'A tradução e os resumos não estão disponíveis no momento.',
    es: 'La traducción y los resúmenes no están disponibles en este momento.',
    ko: '지금은 번역과 요약을 이용할 수 없습니다.',
    fr: 'La traduction et les résumés ne sont pas disponibles pour le moment.',
    ja: '翻訳と要約は現在ご利用いただけません。',
    it: 'La traduzione e i riassunti non sono disponibili in questo momento.',
    de: 'Übersetzung und Zusammenfassungen sind gerade nicht verfügbar.',
    tr: 'Çeviri ve özetler şu anda kullanılamıyor.',
    vi: 'Tính năng dịch và tóm tắt hiện chưa dùng được.',
  },
  "They'll be back by {reset}.": {
    id: 'Fitur ini kembali paling lambat pukul {reset}.',
    pt: 'Eles voltam até as {reset}.',
    es: 'Vuelven a más tardar a las {reset}.',
    ko: '늦어도 {reset}까지는 다시 이용할 수 있습니다.',
    fr: "Ils seront de retour d'ici {reset}.",
    ja: '{reset}までに再びご利用いただけます。',
    it: 'Tornano disponibili entro le {reset}.',
    de: 'Spätestens um {reset} sind sie wieder verfügbar.',
    tr: 'En geç {reset} itibarıyla yeniden kullanılabilir.',
    vi: 'Các tính năng này sẽ hoạt động lại muộn nhất lúc {reset}.',
  },
  'Transcription still works.': {
    id: 'Transkripsi tetap berfungsi.',
    pt: 'A transcrição continua funcionando.',
    es: 'La transcripción sigue funcionando.',
    ko: '음성 텍스트 변환은 계속 사용할 수 있습니다.',
    fr: 'La transcription fonctionne toujours.',
    ja: '文字起こしは引き続き使えます。',
    it: 'La trascrizione funziona ancora.',
    de: 'Die Transkription funktioniert weiterhin.',
    tr: 'Transkripsiyon çalışmaya devam ediyor.',
    vi: 'Tính năng chép lời vẫn hoạt động.',
  },
  // lib/live-translator/languages.ts: the live translator's picker names its languages with
  // Intl.DisplayNames in the page's language, but for Chinese, whose spoken Mandarin it listens
  // to (SEO_BRIEF_10 section 2.3). NATIVE SPEAKER REVIEW RECOMMENDED, as for the rest.
  'Chinese (Mandarin)': {
    id: 'Mandarin',
    pt: 'Chinês (mandarim)',
    es: 'Chino (mandarín)',
    ko: '중국어(만다린)',
    fr: 'Chinois (mandarin)',
    ja: '中国語（普通話）',
    it: 'Cinese (mandarino)',
    de: 'Chinesisch (Mandarin)',
    tr: 'Çince (Mandarin)',
    vi: 'Tiếng Trung (Quan Thoại)',
  },
  // lib/rating-prompt.ts: the optional pop-up after a copy or a download (the founder,
  // 2026-10-30). `{n}` is a star's number, 1 to 5. NATIVE SPEAKER REVIEW RECOMMENDED.
  'How was this tool?': {
    id: 'Bagaimana alat ini menurut Anda?',
    pt: 'O que achou desta ferramenta?',
    es: '¿Qué te pareció esta herramienta?',
    ko: '이 도구는 어떠셨나요?',
    fr: 'Comment avez-vous trouvé cet outil ?',
    ja: 'このツールはいかがでしたか？',
    it: 'Come ti è sembrato questo strumento?',
    de: 'Wie fanden Sie dieses Tool?',
    tr: 'Bu aracı nasıl buldunuz?',
    vi: 'Bạn thấy công cụ này thế nào?',
  },
  '{n} out of 5': {
    id: '{n} dari 5',
    pt: '{n} de 5',
    es: '{n} de 5',
    ko: '5점 만점에 {n}점',
    fr: '{n} sur 5',
    ja: '5段階中{n}',
    it: '{n} su 5',
    de: '{n} von 5',
    tr: '5 üzerinden {n}',
    vi: '{n} trên 5',
  },
  Bad: {
    id: 'Buruk',
    pt: 'Ruim',
    es: 'Mala',
    ko: '나쁨',
    fr: 'Mauvais',
    ja: '悪い',
    it: 'Scarso',
    de: 'Schlecht',
    tr: 'Kötü',
    vi: 'Tệ',
  },
  'Very good': {
    id: 'Sangat bagus',
    pt: 'Muito boa',
    es: 'Muy buena',
    ko: '매우 좋음',
    fr: 'Très bien',
    ja: 'とても良い',
    it: 'Ottimo',
    de: 'Sehr gut',
    tr: 'Çok iyi',
    vi: 'Rất tốt',
  },
  'Thanks for your rating.': {
    id: 'Terima kasih atas penilaian Anda.',
    pt: 'Obrigado pela sua avaliação.',
    es: 'Gracias por tu valoración.',
    ko: '평가해 주셔서 감사합니다.',
    fr: 'Merci pour votre note.',
    ja: '評価していただきありがとうございます。',
    it: 'Grazie per la tua valutazione.',
    de: 'Danke für Ihre Bewertung.',
    tr: 'Değerlendirmeniz için teşekkürler.',
    vi: 'Cảm ơn bạn đã đánh giá.',
  },
  Close: {
    id: 'Tutup',
    pt: 'Fechar',
    es: 'Cerrar',
    ko: '닫기',
    fr: 'Fermer',
    ja: '閉じる',
    it: 'Chiudi',
    de: 'Schließen',
    tr: 'Kapat',
    vi: 'Đóng',
  },
  // lib/option-panel.ts: the position grid's nine cells (read aloud), the colour's clear and its
  // opacity slider, which a translated tool page with those options shows (SEO_BRIEF_11 section 4:
  // /ja/jimaku-ketsugo's merge options). NATIVE SPEAKER REVIEW RECOMMENDED, as for the rest.
  'Top left': {
    id: 'Kiri atas',
    pt: 'Superior esquerdo',
    es: 'Arriba a la izquierda',
    ko: '왼쪽 위',
    fr: 'En haut à gauche',
    ja: '左上',
    it: 'In alto a sinistra',
    de: 'Oben links',
    tr: 'Sol üst',
    vi: 'Trên cùng bên trái',
  },
  'Top centre': {
    id: 'Tengah atas',
    pt: 'Superior central',
    es: 'Arriba al centro',
    ko: '가운데 위',
    fr: 'En haut au centre',
    ja: '中央上',
    it: 'In alto al centro',
    de: 'Oben Mitte',
    tr: 'Orta üst',
    vi: 'Trên cùng ở giữa',
  },
  'Top right': {
    id: 'Kanan atas',
    pt: 'Superior direito',
    es: 'Arriba a la derecha',
    ko: '오른쪽 위',
    fr: 'En haut à droite',
    ja: '右上',
    it: 'In alto a destra',
    de: 'Oben rechts',
    tr: 'Sağ üst',
    vi: 'Trên cùng bên phải',
  },
  'Middle left': {
    id: 'Kiri tengah',
    pt: 'Centro esquerdo',
    es: 'En medio a la izquierda',
    ko: '왼쪽 가운데',
    fr: 'Au milieu à gauche',
    ja: '左中央',
    it: 'Al centro a sinistra',
    de: 'Mitte links',
    tr: 'Sol orta',
    vi: 'Giữa bên trái',
  },
  'Middle centre': {
    id: 'Tengah',
    pt: 'Centro',
    es: 'En medio al centro',
    ko: '정가운데',
    fr: 'Au centre',
    ja: '中央',
    it: 'Al centro',
    de: 'Mitte',
    tr: 'Orta',
    vi: 'Chính giữa',
  },
  'Middle right': {
    id: 'Kanan tengah',
    pt: 'Centro direito',
    es: 'En medio a la derecha',
    ko: '오른쪽 가운데',
    fr: 'Au milieu à droite',
    ja: '右中央',
    it: 'Al centro a destra',
    de: 'Mitte rechts',
    tr: 'Sağ orta',
    vi: 'Giữa bên phải',
  },
  'Bottom left': {
    id: 'Kiri bawah',
    pt: 'Inferior esquerdo',
    es: 'Abajo a la izquierda',
    ko: '왼쪽 아래',
    fr: 'En bas à gauche',
    ja: '左下',
    it: 'In basso a sinistra',
    de: 'Unten links',
    tr: 'Sol alt',
    vi: 'Dưới cùng bên trái',
  },
  'Bottom centre': {
    id: 'Tengah bawah',
    pt: 'Inferior central',
    es: 'Abajo al centro',
    ko: '가운데 아래',
    fr: 'En bas au centre',
    ja: '中央下',
    it: 'In basso al centro',
    de: 'Unten Mitte',
    tr: 'Orta alt',
    vi: 'Dưới cùng ở giữa',
  },
  'Bottom right': {
    id: 'Kanan bawah',
    pt: 'Inferior direito',
    es: 'Abajo a la derecha',
    ko: '오른쪽 아래',
    fr: 'En bas à droite',
    ja: '右下',
    it: 'In basso a destra',
    de: 'Unten rechts',
    tr: 'Sağ alt',
    vi: 'Dưới cùng bên phải',
  },
  'Leave as the style sets it': {
    id: 'Biarkan sesuai gayanya',
    pt: 'Deixar como o estilo define',
    es: 'Dejar como lo define el estilo',
    ko: '스타일 설정대로 두기',
    fr: 'Laisser comme le style le définit',
    ja: 'スタイルの設定のままにする',
    it: 'Lascia come lo imposta lo stile',
    de: 'So lassen, wie der Stil es festlegt',
    tr: 'Stilin belirlediği gibi bırak',
    vi: 'Giữ theo kiểu đã đặt',
  },
  Opacity: {
    id: 'Opasitas',
    pt: 'Opacidade',
    es: 'Opacidad',
    ko: '불투명도',
    fr: 'Opacité',
    ja: '不透明度',
    it: 'Opacità',
    de: 'Deckkraft',
    tr: 'Opaklık',
    vi: 'Độ mờ đục',
  },
};

/**
 * Each entry with every module's word for its key, as if the entry named that language itself.
 * A module's key no entry has is left out, as the server's `add_language` leaves it out.
 */
function withModules(rows: Readonly<Record<string, Row>>): Readonly<Record<string, Translations>> {
  const out: Record<string, Translations> = {};
  for (const [english, row] of Object.entries(rows)) {
    let entry: Translations = row;
    for (const [lang, words] of Object.entries(MODULES) as [ModuleLang, Record<string, string>][]) {
      const word = Object.hasOwn(words, english) ? words[english] : undefined;
      if (word !== undefined) entry = { ...entry, [lang]: word };
    }
    out[english] = entry;
  }
  return out;
}

const STRINGS: Readonly<Record<string, Translations>> = withModules(ROWS);

/** The page's language from `<html lang>`; `en` when there is no document. */
export function pageLang(): PageLang {
  const lang = globalThis.document?.documentElement?.lang;
  return lang !== undefined && PAGE_LANGS.includes(lang) ? (lang as PageLang) : 'en';
}

/** `english` in the page's language, or `english` itself when there is none. */
export function t(english: string, lang: PageLang = pageLang()): string {
  if (lang === 'en') return english;
  return STRINGS[english]?.[lang] ?? english;
}

/** For the tests: every key, so a caller's literal can be checked against it. */
export const I18N_KEYS: readonly string[] = Object.keys(STRINGS);
